import React, {useCallback} from "react";
import IconButton from "@mui/material/IconButton";
import {
    Alert,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    FormControl, FormHelperText, 
    Grid,
    InputLabel, Select,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import {useState, useEffect, useMemo} from "react";
import MenuItem from "@mui/material/MenuItem";
import { useDispatch, useSelector} from "react-redux";
import { updateUser } from "../../index";
import { setRightDrawer} from "../../../../Common/App";
import {Loader} from "../../../../../Components";
import {fetchRoles} from "../../../Roles";

export const EditUser = () => {

    const dispatch = useDispatch();
    const item = useSelector((store) => store.users.selected);
    const rolesList = useSelector((store) => store.roles.list);
    const roles = useMemo(() => rolesList || [], [rolesList]);
    const departments = useSelector((store) => store.app.config.departments);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState("info");
    const [data, setData] = useState({
        id: "",
        first_name: "",
        last_name: "",
        email: "",
        mobile: "",
        event: "",
        designation: "",
        roles: [],
        role_type: "",
        deleted_at: null,
        department_id: "",
    });

    useEffect(() => {
        // Filter out restricted roles that are not visible in dropdown
        const availableRoleIds = roles.map(r => r.id);
        const userRoleIds = item.roles?.map((role) => role.id) ?? [];
        const filteredRoleIds = userRoleIds.filter(id => availableRoleIds.includes(id));
        
        setData({
            id: item.id,
            first_name: item.first_name,
            last_name: item.last_name,
            email: item.email,
            mobile: item.mobile,
            event: item.event ?? "",
            designation: item.designation ?? "",
            roles: filteredRoleIds,
            role_type: item.role_type ?? "",
            deleted_at: item.deleted_at,
            department_id: item.department_id ?? "",
        });
    },[item, roles])

    const fetchRoleList = useCallback(async () => {
        await fetchRoles({dispatch, filters:{paginate:false}})
    }, [dispatch]);

    useEffect(() => {
        fetchRoleList()
    }, [fetchRoleList]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData(data => ({
            ...data,
            [name]: value,
        }));
    }

    const handleSubmit = async () => {
        setIsLoading(true);
        await updateUser({dispatch, data}).then((res) => {
            setIsLoading(false);
            if(res.success){
                dispatch(setRightDrawer({component:null, open:false}));
            }
            setMessage(res.message);
            setErrors(res.errors || {});
            setStatus(res.status)
        });
    };

    return(
        <Card sx={{ height: '100%', position:'relative', display: 'flex', flexDirection:'column', alignItems:'stretch', justifyContent:'flex-start', alignContent:'stretch' }}>
            <CardHeader
                action={
                    <IconButton aria-label="share" onClick={()=> dispatch(setRightDrawer({component:null, open:false}))}>
                        <CloseIcon />
                    </IconButton>
                }
                title={"Edit User"}
                avatar={
                    <Loader isLoading={isLoading} type="linear" />
                }
            />
            <CardContent sx={{ flex:1 }}>
                <Grid container spacing={3}>
                    {message && (
                        <Grid item xs={12}>
                            <Alert severity={status}>{message}</Alert>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="First Name"
                            name="first_name"
                            onChange={(e) => handleChange(e)}
                            variant="outlined"
                            size="large"
                            value={ data['first_name'] }
                            helperText={errors?.first_name}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            name="last_name"
                            onChange={(e) => handleChange(e)}
                            value={ data['last_name'] }
                            variant="outlined"
                            size="large"
                            helperText={errors?.last_name}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            onChange={(e) => handleChange(e)}
                            value={ data['email'] }
                            variant="outlined"
                            size="large"
                            helperText={errors?.email}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Mobile"
                            name="mobile"
                            type="tel"
                            onChange={(e) => handleChange(e)}
                            value={ data['mobile'] }
                            variant="outlined"
                            size="large"
                            required
                            helperText={errors?.mobile}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Designation"
                            name="designation"
                            onChange={(e) => handleChange(e)}
                            value={ data['designation'] }
                            variant="outlined"
                            size="large"
                            helperText={errors?.designation}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="department_id">Department</InputLabel>
                            <Select
                                label="Department"
                                name="department_id"
                                value={data?.department_id}
                                onChange={handleChange}
                                variant="outlined"
                            >
                                {departments && departments.length > 0 && departments.map((department) => (
                                    <MenuItem key={department.id} value={department.id}>{department.department_name}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors?.department_id}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="roles">Role</InputLabel>
                            <Select
                                multiple
                                value={data['roles']}
                                name="roles"
                                MenuProps={{
                                    style: {zIndex: 35001}
                                }}
                                fullWidth
                                label="Role"
                                onChange={(e) => handleChange(e)}
                                variant="outlined"
                            >
                                {roles && roles.length > 0 && roles.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>{role.role_name}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors?.roles}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="role_type">Role Type</InputLabel>
                            <Select
                                value={ data['role_type'] }
                                name="role_type"
                                MenuProps={{
                                    style: {zIndex: 35001}
                                }}
                                fullWidth
                                label="Role Type"
                                onChange={(e) => handleChange(e)}
                                variant="outlined"
                            >
                                <MenuItem key={"ADMIN"} value={"ADMIN"}>{"Admin"}</MenuItem>
                                <MenuItem key={"EMPLOYEE"} value={"EMPLOYEE"}>{"Employee"}</MenuItem>
                                <MenuItem key={"MANAGER"} value={"MANAGER"}>{"Manager"}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions className={'card-footer'} sx={{ p:2, display:'flex', justifyContent:'space-between'}}>
                <Button variant={'contained'} color={'inherit'} onClick={()=> dispatch(setRightDrawer({component:null, open:false}))}>Close</Button>
                <Button color="primary" variant="contained" onClick={ handleSubmit }>Save</Button>
            </CardActions>
        </Card>
    )
}
